import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  MapPin,
  Clock,
  CheckCircle,
  Circle,
  Truck,
  Phone,
  Wheelchair,
  Hospital,
  Calendar,
  ShieldCheck,
} from "@phosphor-icons/react";
import { apiClient, formatApiErrorDetail } from "../lib/api";
import TripMap from "../components/TripMap";
import { ChainOfCustodyStitcher, BreathingDot } from "../components/live/LiveWorld";
import { DUR, EASE } from "../lib/motion";

const EVENT_LABEL = {
  "booking.created": "Request received",
  "trip.forwarded": "Dispatched to OVA",
  "trip.forward_failed": "Dispatch issue",
  "trip.polled": "Status refreshed",
  "trip.webhook": "Update from OVA",
  "trip.driver_assigned": "Driver assigned",
  "trip.driver_arrived": "Driver at pickup",
  "trip.in_progress": "Trip in progress",
  "trip.completed": "Trip completed",
  "trip.cancelled": "Trip cancelled",
};

const STATUS_ORDER = ["pending", "confirmed", "in_progress", "completed"];
const STATUS_LABEL = {
  pending: "Awaiting dispatch",
  confirmed: "Dispatched",
  in_progress: "In progress",
  completed: "Completed",
  cancelled: "Cancelled",
};

export default function TrackTrip() {
  const { id } = useParams();
  const [trip, setTrip] = useState(null);
  const [error, setError] = useState(null);
  const [eta, setEta] = useState(null);
  const timerRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await apiClient.get(`/public/trip/${id}`);
      setTrip(data);
      setError(null);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Trip not found");
    }
  };

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, 25000);
    return () => clearInterval(timerRef.current);
  }, [id]);

  if (error) {
    return (
      <main className="min-h-screen bg-brandblue-ink text-white grid place-items-center px-6">
        <div className="text-center max-w-md">
          <img src="/logo.png" alt="MedexOVA" className="h-16 w-16 mx-auto logo-glow" />
          <h1 className="mt-6 font-heading text-3xl font-bold">Trip not found</h1>
          <p className="mt-3 text-white/60 text-sm">{error}</p>
          <Link to="/" className="mt-6 inline-flex items-center gap-2 text-brandblue hover:text-white">
            <ArrowLeft size={14} /> Back to MedexOVA
          </Link>
        </div>
      </main>
    );
  }

  if (!trip) {
    return (
      <main className="min-h-screen bg-brandblue-ink text-white grid place-items-center">
        <div className="text-white/60 text-sm">Loading trip…</div>
      </main>
    );
  }

  const currentStatus = trip.status === "cancelled" ? "cancelled" : trip.status || "pending";
  const currentIdx = STATUS_ORDER.indexOf(currentStatus);

  return (
    <main className="min-h-screen bg-brandblue-ink text-white relative overflow-hidden" data-testid="track-trip-page">
      <div aria-hidden className="orb h-[480px] w-[480px] bg-brandblue/30 -top-24 -left-20" />
      <div aria-hidden className="orb h-[380px] w-[380px] bg-ova/25 bottom-0 right-0" />
      <div aria-hidden className="absolute inset-0 grid-bg opacity-50" />

      <header className="relative z-10">
        <div className="max-w-4xl mx-auto px-6 py-6 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 group">
            <span className="relative inline-flex h-9 w-9 items-center justify-center rounded-md bg-transparent overflow-hidden">
              <img src="/logo.png" alt="MedexOVA" className="h-8 w-8 object-contain logo-glow" />
            </span>
            <span className="font-heading text-base tracking-tight font-black">
              <span className="text-[#16A34A]">Medex</span><span className="text-[#C4B5FD]">OVA</span>
            </span>
          </Link>
          <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-semibold text-white/60">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-brandblue/60 live-dot"></span>
              <span className="relative inline-flex h-2 w-2 rounded-full bg-brandblue"></span>
            </span>
            Live tracking
          </span>
        </div>
      </header>

      <section className="relative z-10 max-w-4xl mx-auto px-6 pb-20">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="text-[11px] uppercase tracking-[0.3em] font-bold text-brandblue">Trip #{trip.id.slice(0, 8)}</div>
          <h1 className="mt-3 font-heading text-4xl sm:text-5xl font-black leading-tight">
            {STATUS_LABEL[currentStatus] || currentStatus}
            {currentStatus === "completed" && <CheckCircle size={40} weight="fill" className="inline ml-3 text-forest" />}
          </h1>
          <p className="mt-2 text-white/60 text-sm">
            Scheduled {trip.pickup_date} · {trip.pickup_time} · {trip.trip_type?.replace(/_/g, " ")}
          </p>
          {/* Live ETA banner when driver is active */}
          {eta && trip.driver_lat && eta.secToPickup != null && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-6 inline-flex items-center gap-4 rounded-xl border border-brandblue/30 bg-brandblue/10 backdrop-blur-md px-5 py-3"
              data-testid="live-eta-banner"
            >
              <div className="relative flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-brandblue/60 live-dot"></span>
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-brandblue"></span>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-brandblue">Driver arriving in</div>
                <div className="font-heading text-2xl sm:text-3xl font-black leading-none mt-1">
                  {formatMinLabel(eta.secToPickup)}
                </div>
              </div>
              {eta.pickupDistance && (
                <div className="pl-5 border-l border-white/10">
                  <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-white/50">Distance</div>
                  <div className="text-base font-mono font-semibold mt-1" style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>
                    {eta.pickupDistance}
                  </div>
                </div>
              )}
              {eta.secPickupToDropoff && (
                <div className="pl-5 border-l border-white/10 hidden sm:block">
                  <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-white/50">Then to drop-off</div>
                  <div className="text-base font-mono font-semibold mt-1" style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>
                    ~{formatMinLabel(eta.secPickupToDropoff)}
                  </div>
                </div>
              )}
            </motion.div>
          )}
          {/* Estimated drive banner when no driver yet */}
          {eta && !trip.driver_lat && eta.secPickupToDropoff && (
            <div className="mt-6 inline-flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.04] backdrop-blur-md px-5 py-2.5 text-sm text-white/80" data-testid="est-drive-banner">
              <Clock size={14} className="text-brandblue" />
              Estimated drive time · <span className="font-mono" style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{formatMinLabel(eta.secPickupToDropoff)}</span>
              {eta.tripDistance && <span className="text-white/40">· {eta.tripDistance}</span>}
            </div>
          )}
        </motion.div>

        {/* Stepper */}
        <div className="mt-10 grid grid-cols-4 gap-2">
          {STATUS_ORDER.map((s, i) => {
            const active = currentStatus !== "cancelled" && i <= Math.max(0, currentIdx);
            return (
              <div key={s} className="flex flex-col items-start">
                <div className="flex items-center gap-2 w-full">
                  <div className={`h-2 w-2 rounded-full ${active ? "bg-brandblue" : "bg-white/20"}`}></div>
                  <div className={`flex-1 h-[2px] ${active ? "bg-brandblue" : "bg-white/10"}`}></div>
                </div>
                <div className={`mt-2 text-[11px] uppercase tracking-[0.18em] font-semibold ${active ? "text-white" : "text-white/40"}`}>
                  {STATUS_LABEL[s]}
                </div>
              </div>
            );
          })}
        </div>

        {/* Chain of custody · builds live as ceremonies fire */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: DUR.reveal, ease: EASE.emerge, delay: 0.15 }}
          className="mt-8 rounded-xl border border-white/10 bg-white/[0.04] backdrop-blur-xl p-5"
          data-testid="track-coc-stitcher"
        >
          <div className="flex items-center gap-2 mb-4">
            <BreathingDot color="#3B82F6"/>
            <span className="text-[11px] uppercase tracking-[0.2em] font-bold text-brandblue">Chain of custody · live</span>
          </div>
          <ChainOfCustodyStitcher
            rows={buildCocRows(trip, currentIdx)}
            accent="#3B82F6"
          />
        </motion.div>

        <div className="mt-10 grid md:grid-cols-5 gap-6">
          {/* Left: map + trip details */}
          <div className="md:col-span-3 space-y-5">
            <TripMap
              theme="dark"
              height={340}
              pickup={trip.pickup_lat ? { lat: trip.pickup_lat, lng: trip.pickup_lng, label: trip.pickup_address } : null}
              dropoff={trip.dropoff_lat ? { lat: trip.dropoff_lat, lng: trip.dropoff_lng, label: trip.dropoff_address } : null}
              driver={trip.driver_lat ? { lat: trip.driver_lat, lng: trip.driver_lng } : null}
              onEta={setEta}
            />
            <TileSection title="Route">
              <RouteRow icon={Circle} label="Pickup" text={trip.pickup_address} tone="text-forest" />
              <RouteRow icon={MapPin} label="Drop-off" text={trip.dropoff_address} tone="text-terracotta" />
              {trip.return_trip && <RouteRow icon={Circle} label="Return trip" text="Requested" />}
            </TileSection>
            <TileSection title="Rider">
              <Row label="Patient"><span className="font-mono">{trip.patient_initials}</span></Row>
              <Row label="Mobility">
                <span className="inline-flex items-center gap-2">
                  <Wheelchair size={14} className="text-brandblue" /> {trip.mobility_type}
                </span>
              </Row>
              <Row label="Facility">
                <span className="inline-flex items-center gap-2">
                  <Hospital size={14} className="text-brandblue" /> {trip.facility_name}
                </span>
              </Row>
            </TileSection>
            {trip.driver_name && (
              <TileSection title="Your driver">
                <Row label="Name">{trip.driver_name}</Row>
                {trip.driver_phone && (
                  <Row label="Phone">
                    <a href={`tel:${trip.driver_phone}`} className="text-brandblue inline-flex items-center gap-1 hover:text-white">
                      <Phone size={14} /> {trip.driver_phone}
                    </a>
                  </Row>
                )}
              </TileSection>
            )}
          </div>

          {/* Right: timeline */}
          <div className="md:col-span-2">
            <TileSection title="Timeline">
              {trip.events && trip.events.length ? (
                <ol className="relative border-l border-white/10 ml-2">
                  {trip.events.map((e, i) => (
                    <li key={i} className="ml-4 pb-4 last:pb-0">
                      <span className="absolute -left-[5px] h-2.5 w-2.5 rounded-full bg-brandblue ring-4 ring-brandblue-ink"></span>
                      <div className="text-sm text-white">{EVENT_LABEL[e.event] || e.event}</div>
                      <div className="text-[11px] text-white/40 font-mono mt-0.5" style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>
                        {new Date(e.at).toLocaleString()}
                      </div>
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="text-sm text-white/60">No events yet. This page updates every 25 seconds.</p>
              )}
            </TileSection>
          </div>
        </div>

        <div className="mt-8 flex items-center justify-between text-[11px] text-white/40">
          <span className="inline-flex items-center gap-1.5">
            <ShieldCheck size={12} className="text-forest" /> HIPAA-aware · patient details masked
          </span>
          <span>Auto-refresh every 25s</span>
        </div>
      </section>
    </main>
  );
}

function formatMinLabel(seconds) {
  if (!seconds || seconds < 0) return "—";
  const m = Math.round(seconds / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return `${h}h ${rem}m`;
}

// Build chain-of-custody rows from trip status. Each row lands as the stepper
// advances so the rider sees the receipt assemble live.
function buildCocRows(trip, statusIdx) {
  const stampAt = (v) => v ? new Date(v).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "pending";
  const rows = [];
  rows.push({ label: "Trip requested",          stamp: stampAt(trip?.created_at),      provider: "MedexOVA" });
  if (statusIdx >= 1) rows.push({ label: "Dispatched to network",    stamp: stampAt(trip?.dispatched_at || trip?.created_at), provider: "OVA" });
  if (trip?.driver_name)  rows.push({ label: "Driver credentialed",  stamp: stampAt(trip?.driver_assigned_at || trip?.dispatched_at), provider: "MedexOva" });
  if (statusIdx >= 2 || trip?.driver_lat) rows.push({ label: "Pickup ceremony",        stamp: stampAt(trip?.pickup_at || trip?.driver_assigned_at), provider: "GPS" });
  if (statusIdx >= 3) rows.push({ label: "POD ceremony · stitched",  stamp: stampAt(trip?.completed_at),   provider: "POD" });
  if (statusIdx >= 3) rows.push({ label: "Hash locked · receipt sealed", stamp: (trip?.id || "").slice(0, 8), provider: "SHA-256" });
  return rows;
}

function TileSection({ title, children }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.04] backdrop-blur-xl p-5">
      <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-brandblue mb-3">{title}</div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}
function Row({ label, children }) {
  return (
    <div className="grid grid-cols-[90px_1fr] gap-3 text-sm">
      <div className="text-white/50">{label}</div>
      <div className="text-white break-words">{children}</div>
    </div>
  );
}
function RouteRow({ icon: Icon, label, text, tone = "text-brandblue" }) {
  return (
    <div className="flex items-start gap-3">
      <Icon weight="fill" size={14} className={`mt-1 ${tone}`} />
      <div>
        <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-white/50">{label}</div>
        <div className="text-sm text-white">{text}</div>
      </div>
    </div>
  );
}
